import { SASelectedFacets } from 'types/SearchProvider'
import {
  BuildQueryParamsFacetsObjectParams,
  BuildSelectedFacetsObjectParams,
  GetSizeFacetOptionsParams,
  UpdateFacetValuesParams,
} from '../types'
import { getCategorySpecificFacets } from 'lib/constructor/constants/hardcodedSaatchiFilters'

export const buildSelectedFacetsObject = ({
  selectedFacets,
  facetName,
  facetValue,
  inputType,
  resetFacet,
}: BuildSelectedFacetsObjectParams): SASelectedFacets => {
  const currentFacetValues = selectedFacets?.[facetName] || []
  const valueExists = currentFacetValues.includes(facetValue)

  const newValues = valueExists
    ? currentFacetValues.filter((value) => value !== facetValue)
    : [...currentFacetValues, facetValue]

  const newSelectedFacets = { ...selectedFacets }

  if (Boolean(resetFacet)) {
    delete newSelectedFacets[facetName]
    return newSelectedFacets
  }

  // Clear category-specific facets when category changes
  if (facetName === 'category') {
    const oldCategory = selectedFacets?.category?.[0]
    const oldCategorySpecificFacets = getCategorySpecificFacets(oldCategory)

    oldCategorySpecificFacets.forEach((facetToRemove) => {
      delete newSelectedFacets[facetToRemove]
    })
  }

  if (inputType === 'single') {
    if (valueExists) {
      delete newSelectedFacets[facetName]
    } else {
      newSelectedFacets[facetName] = [facetValue]
    }

    return newSelectedFacets
  }

  if (newValues.length === 0) {
    delete newSelectedFacets[facetName]
  } else {
    newSelectedFacets[facetName] = newValues
  }

  return newSelectedFacets
}

export const updateFacetValues = ({
  selectedFacets,
  facetName,
  attributeValue,
}: UpdateFacetValuesParams): SASelectedFacets => {
  const isFacetValueSelected = selectedFacets?.[facetName]?.includes(attributeValue)

  return {
    ...selectedFacets,
    [facetName]: isFacetValueSelected
      ? selectedFacets[facetName].filter((attr) => attr !== attributeValue)
      : [...selectedFacets[facetName], attributeValue],
  }
}

export const getSizeFacetOptions = ({
  facetOptions,
  userSizePreference,
  isPrintsFilter,
}: GetSizeFacetOptionsParams) => {
  const multiplier = userSizePreference === 'cm' ? 2.54 : 1

  const geRoundNumber = (number) => {
    return Number(parseFloat(`${number * multiplier}`).toFixed(0))
  }

  const inchesThresholdForMedium = isPrintsFilter ? 20 : 20
  const inchesThresholdForLarge = isPrintsFilter ? 40 : 38
  const inchesThresholdForOversized = isPrintsFilter ? 45 : 60

  const sizeOptions = [
    {
      label: `Small (<${geRoundNumber(inchesThresholdForMedium)} ${userSizePreference})`,
      value: 'small',
    },
    {
      label: `Medium (${geRoundNumber(inchesThresholdForMedium)}-${geRoundNumber(
        inchesThresholdForLarge
      )} ${userSizePreference})`,
      value: 'medium',
    },
    {
      label: `Large (${geRoundNumber(inchesThresholdForLarge)}-${geRoundNumber(
        inchesThresholdForOversized
      )} ${userSizePreference})`,
      value: 'large',
    },
    {
      label: `Oversized (>${geRoundNumber(inchesThresholdForOversized)} ${userSizePreference})`,
      value: 'oversized',
    },
  ]

  const finalSizeOptions = sizeOptions.filter(({ value }) =>
    facetOptions.some(({ value: facetValue }) => value === facetValue)
  )

  return finalSizeOptions
}

export const buildQueryParamsFacetsObject = ({
  basicQueryParams,
  facetName,
  facetValue,
  isQueryParam,
}: BuildQueryParamsFacetsObjectParams) => {
  const newQueryParams = { ...basicQueryParams }

  if (isQueryParam) {
    const valueExists = basicQueryParams?.[facetName]?.includes(facetValue)

    if (valueExists) {
      delete newQueryParams[facetName]
    } else {
      newQueryParams[facetName] = [facetValue]
    }
  }

  return { ...newQueryParams, page: '' }
}
